import type { HttpMethod } from '@/types/api';

/**
 * Browser-side protection for the backend read-web admission budget.
 *
 * The gate is intentionally local to one JavaScript realm. The backend remains
 * authoritative across tabs, processes, and app replicas.
 */
export const READ_WEB_REQUEST_PREFIXES = [
  '/api/web-admin/stock',
  '/api/web-admin/analytics',
] as const;

export const READ_WEB_LOCAL_MAX_INFLIGHT_REQUESTS = 2;

/**
 * How long one network attempt of a read may keep its permit. A read still
 * unanswered after that goes on, but no longer keeps the next read waiting.
 */
export const READ_WEB_PERMIT_MAX_HOLD_MS = 10_000;

const CORE_WEB_READ_EXCEPTIONS = ['/api/web-admin/stock/health'] as const;

export function pathMatchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function getRequestPathname(url: string): string {
  try {
    return new URL(url, 'http://localhost').pathname;
  } catch {
    return url.split(/[?#]/, 1)[0] ?? url;
  }
}

export function isReadWebRequest(url: string, method: HttpMethod): boolean {
  if (method !== 'GET') return false;

  const pathname = getRequestPathname(url);
  if (CORE_WEB_READ_EXCEPTIONS.some((prefix) => pathMatchesPrefix(pathname, prefix))) {
    return false;
  }
  return READ_WEB_REQUEST_PREFIXES.some((prefix) => pathMatchesPrefix(pathname, prefix));
}
